import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Tree, flatMapTree } from "../custom-components/tree-view-component";
import { Module, QueryKeys } from "../models/models";
import { TreeNode } from "../models/tree-models";
import { useQuery, useStore } from "../stores/store";
import { getSortedData } from "./app-container-components/configure-formula";
import { supportedModules } from "../stores/constants";
import { Flex, NavLink, createStyles } from "@mantine/core";

export const useSideBarStyles = createStyles((theme) => ({
  collapsedSideBarLink: {
    padding: theme.other.arc.paddings[1],
    "&[data-active]": {
      backgroundColor: theme.other.arc.colors[7],
    },
    "&[data-active]: hover": {
      backgroundColor: theme.other.arc.colors[7],
    },
    ":hover": {
      backgroundColor: theme.other.arc.colors[6],
    },
    ".mantine-NavLink-label": {
      fontSize: "6px",
    },
  },
  collapsedSideBarIcons: {
    height: "auto",
    width: "100%",
    padding: "10px",
  },
}));
export const GetAllChilds = (modules: Module[], parentId: number) => {
  return modules.filter((d) => d.parentId === parentId);
};
export const GetSubElements = (modules: Module[], parentId: number) => {
  let all: Module[] = [];
  all = modules
    .filter((d) => d.parentId === parentId)
    .map((s) => {
      return { ...s, parentId: 0 };
    });
  for (let i = 0; i < all.length; i++) {
    all = [...all, ...GetAllChilds(modules, all[i].id)];
  }
  return JSON.parse(JSON.stringify(all)) as Module[];
};

export const getParents = (moduleTreeData: Module[]): TreeNode[] => {
  const parentData = moduleTreeData
    .filter((d) => d.parentId == 0)
    .map((fd) => {
      return { id: fd.id.toString(), name: fd.name, children: [] };
    });
  return parentData;
};

export const getChildren = (moduleTreeData: Module[], parentId: number): TreeNode[] => {
  let children: TreeNode[] = [];
  children = moduleTreeData
    .filter((md) => md.parentId == parentId)
    .map((fd) => {
      return { id: fd.id.toString(), name: fd.name, children: getChildren(moduleTreeData, fd.id) };
    });
  return children;
};

export const SideBar = observer(function FormulaEditor({ treeView, onIconClick, showAll }: { treeView: boolean; onIconClick: () => void; showAll?: boolean }) {
  const { store } = useStore();
  const [data, setData] = useState<TreeNode[]>([]);
  const { pathname } = useLocation();
  const { moduleId } = useParams();
  const [treeNode, setTreeNode] = useState<{ selected: string; expanded: string[] | "all" }>({
    selected: pathname.split("/")[pathname.split("/").length - 1] ?? "",
    expanded: "all",
  });
  const { classes } = useSideBarStyles();

  const navigateTo = useNavigate();

  const { data: modules } = useQuery({
    queryKey: [QueryKeys.Modules],
    queryFn: async () => {
      return await store.GetAllModules();
    },
  });
  useEffect(() => {
    const orderedModules = getSortedData(
      modules?.find((m) => m.parentId === 0)?.moduleConfig?.modulesOrder?.map((m) => m.toString()) ?? [],
      modules ?? []
    ) as Module[];
    transFormData(orderedModules ?? []);
  }, [JSON.stringify(modules), showAll]);

  const transFormData = (moduleTreeData: Module[]) => {
    const parents = getParents(moduleTreeData);
    const transformedData = parents.map((pd) => {
      const cData = getChildren(moduleTreeData, Number(pd.id));
      return { ...pd, children: cData };
    });
    setData(showAll ? [{ children: [], id: "all", name: "All Reports" }, ...transformedData] : transformedData);
  };

  const reOrder = (modulesOrder: number[]) => {
    store.UpdateModulesSortOrder(modulesOrder);
  };

  const renderHighLevelIcons = (module: TreeNode) => {
    const moduleChildrens = module.children != null && module.children.length > 0 ? flatMapTree(module.children) : [];
    const activeId = moduleChildrens.length === 0 ? module.id : moduleChildrens.filter((c) => c.children?.length === 0)[0].id;
    return (
      <NavLink
        className={classes.collapsedSideBarLink}
        variant="filled"
        label={<img className={classes.collapsedSideBarIcons} src={supportedModules.find((sm) => sm.title === module.name)?.img} alt={`${module.name}`} />}
        key={module.id}
        active={module.id == treeNode.selected || moduleChildrens.find((c) => c.id == treeNode.selected) != null}
        onClick={() => {
          onIconClick();
          setTreeNode({ selected: activeId, expanded: [module.id] });
          if (moduleId == null) {
            navigateTo(`/main/formulaEditor/${activeId}`);
          } else {
            navigateTo(`/reports/${activeId}`);
          }
        }}
      />
    );
  };

  return (
    <>
      {treeView ? (
        <Tree
          data={data}
          value={treeNode.selected}
          onSelect={(value) => {
            setTreeNode({ ...treeNode, selected: value });
            if (moduleId == null) {
              navigateTo(`/main/formulaEditor/${value}`);
            } else {
              navigateTo(`/reports/${value}`);
            }
          }}
          onChange={(e) => {
            setData(e);
          }}
          defaultExpandedNodes={treeNode.expanded}
          reOrder={reOrder}
        />
      ) : (
        <Flex direction={"column"} wrap={"nowrap"} align={"center"}>
          {data?.map((d) => {
            return renderHighLevelIcons(d);
          })}
        </Flex>
      )}
    </>
  );
});
